// ==================================================================================
// Digital Approval System (DAS) - Attachments Service
// ==================================================================================
// Service functions for attachment operations
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse } from '../../types/common.types';
import {
  Attachment,
  UploadAttachmentRequest,
  UpdateAttachmentRequest,
  AttachmentStats,
  FileUploadSettings,
} from '../../types/attachments.types';
import { attachmentEndpoints } from '../../endpoints/attachments.endpoints';
import type { ID } from '../../types';

/**
 * Attachments service
 */
export const attachmentsService = {
  /**
   * Upload a file attachment
   */
  upload: async (
    file: File,
    request: UploadAttachmentRequest,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('requestId', request.requestId.toString());
    formData.append('documentTitle', request.documentTitle);

    if (request.documentDescription) {
      formData.append('documentDescription', request.documentDescription);
    }

    if (request.workflowActionId) {
      formData.append('workflowActionId', request.workflowActionId.toString());
    }

    const response = await axiosClient.post<ApiResponse<Attachment>>(
      attachmentEndpoints.upload(),
      formData,
      {
        // Do NOT set Content-Type manually for FormData - browser sets it automatically with boundary
        onUploadProgress,
      }
    );
    return response.data.data;
  },

  /**
   * Get attachment by ID
   */
  getById: async (id: ID): Promise<Attachment> => {
    const response = await axiosClient.get<ApiResponse<Attachment>>(
      attachmentEndpoints.getById(id)
    );
    return response.data.data;
  },

  /**
   * Get all attachments for a request
   */
  getByTicketId: async (requestId: ID): Promise<Attachment[]> => {
    const response = await axiosClient.get<ApiResponse<Attachment[]>>(
      attachmentEndpoints.getByTicketId(requestId)
    );
    return response.data.data;
  },

  /**
   * Download attachment file
   * Returns blob URL for file download
   */
  download: async (id: ID, fileName: string): Promise<string> => {
    const response = await axiosClient.get(attachmentEndpoints.download(id), {
      responseType: 'blob',
    });

    // Create blob URL
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Return URL in case caller needs it
    return url;
  },

  /**
   * Get blob URL for preview (without triggering download)
   * Returns blob URL for displaying in preview modal
   */
  getPreviewUrl: async (id: ID, mimeType?: string): Promise<string> => {
    const response = await axiosClient.get(attachmentEndpoints.download(id), {
      responseType: 'blob',
    });

    // Create blob with proper MIME type for preview
    const blob = new Blob([response.data], { type: mimeType || response.headers['content-type'] });
    return window.URL.createObjectURL(blob);
  },

  /**
   * Update attachment metadata
   */
  update: async (id: ID, request: UpdateAttachmentRequest): Promise<Attachment> => {
    const response = await axiosClient.put<ApiResponse<Attachment>>(
      attachmentEndpoints.update(id),
      request
    );
    return response.data.data;
  },

  /**
   * Delete attachment (soft delete)
   */
  delete: async (id: ID): Promise<void> => {
    await axiosClient.delete<ApiResponse<void>>(attachmentEndpoints.delete(id));
  },

  /**
   * Get attachment statistics for a request
   */
  getStats: async (requestId: ID): Promise<AttachmentStats> => {
    const response = await axiosClient.get<ApiResponse<AttachmentStats>>(
      attachmentEndpoints.getStats(requestId)
    );
    return response.data.data;
  },

  /**
   * Get file upload settings (max size, allowed extensions)
   */
  getSettings: async (): Promise<FileUploadSettings> => {
    const response = await axiosClient.get<ApiResponse<FileUploadSettings>>(
      attachmentEndpoints.getSettings()
    );
    return response.data.data;
  },
};
